import React, { useMemo } from "react";
import { WorkspaceMember } from "@common/types/workspace";
import { sortBy, uniqBy } from "@common/helpers/collections";
import { Organization } from "@common/types/organization";
import { userWorkspaceRolesInOrder } from "@common/userWorkspaceRoles";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { EntityTeamMember } from "./EntityTeamMember";
import { EntityOrganization } from "./EntityOrganization";

interface Props {
    members: WorkspaceMember[];
}

type EntityTeamItem =
    | { kind: "organization"; organization: Organization }
    | { kind: "member"; member: WorkspaceMember };

const MAX_ASSIGNMENTS = 7;

const getKey = (item: EntityTeamItem) =>
    item.kind === "organization"
        ? `organization-${item.organization.id}`
        : `member-${item.member.user.id}`;

const getLabel = (item: EntityTeamItem) =>
    item.kind === "organization"
        ? item.organization.name
        : (item.member.user.name ?? item.member.user.email);

const renderAvatar = (item: EntityTeamItem) =>
    item.kind === "organization" ? (
        <EntityOrganization organization={item.organization} />
    ) : (
        <EntityTeamMember user={item.member.user} role={item.member.role} />
    );

/** The firms and people on an entity's team, firms first, in one stack. */
export const EntityTeam: React.FC<Props> = ({ members }) => {
    const items = useMemo((): EntityTeamItem[] => {
        const organizations = uniqBy(
            members
                ?.map((member) => member.organization)
                .filter((org): org is Organization => !!org) ?? [],
            (org) => org.id,
        );
        const sortedMembers = sortBy(
            members?.filter((member) => !member.organizationId) ?? [],
            (member) => userWorkspaceRolesInOrder.indexOf(member.role),
            (member) => member.user.id,
        );

        return [
            ...organizations.map((organization): EntityTeamItem => ({
                kind: "organization",
                organization,
            })),
            ...sortedMembers.map((member): EntityTeamItem => ({
                kind: "member",
                member,
            })),
        ];
    }, [members]);

    return (
        <AvatarGroup
            items={items}
            getKey={getKey}
            getLabel={getLabel}
            renderAvatar={renderAvatar}
            max={MAX_ASSIGNMENTS}
            size="sm"
        />
    );
};
